import {
  Box,
  CircularProgress,
  Grid,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SelectDocumentType from '../../../../../Student/SelectDocumentType';
import { useDataValidations } from '../../../../../../shared/hooks/useDataValidations';
import Localities from '../../../../../../components/Localities/Localities';
import './StudentData.css';
import React, { ChangeEvent, useEffect, useRef } from 'react';
import { useAppContext } from '../../../../../App.Context';
import Phonefield from '../../../../../../components/Fields/PhoneField';
import DocumentTypes from '../../../../../../models/DocumentTypes';
import { RequestData } from '../../../../useRequestData';
type GenericEvent = ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<HTMLSelectElement>;

const FormContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Grid container spacing={3}>
      {children}
    </Grid>
  );
};

interface StudentDataProps {
  requestData: RequestData
}

const StudentData: React.FC<StudentDataProps> = ({ requestData }) => {
  const { isEmailError, isPhoneError, isNameError, isIdentificationError, isLastNameError, fieldsAreEquals } = useDataValidations();
  const { t } = useTranslation();
  const { user } = useAppContext();
  const { student, setStudent, setCanSaveStudentData, studentRecord, repeatEmail, setRepeatEmail, rejectedUserProfile } = requestData;
  const isLoadingUserData = user.valid && !student?.name && !rejectedUserProfile;
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);


  const checkLoadingData = (): React.ReactNode => {
    return isLoadingUserData && <CircularProgress color="inherit" size={20} />;
  };

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);


  const handleStudent = (e: GenericEvent) => {
    const value = e.target.value;
    const key = e.target.name;

    if (key === 'document_type') {
      setStudent({
        ...student,
        document_type: value,
        document_number: ''
      });
      return;
    }

    setStudent({
      ...student,
      [key]: value
    });

    if (!student?.document_type) {
      setStudent({
        ...student,
        document_type: DocumentTypes.dni
      });
    }
  };

  const errors: { [key: string]: string } = {
    email: (!studentRecord?.email && student?.email) && isEmailError(student.email),
    repeatEmail: repeatEmail && ((isEmailError(repeatEmail) || fieldsAreEquals(repeatEmail, student?.email, t('email')) || '')),
    name: (!studentRecord?.name && student?.name) && isNameError(student.name),
    last_name: (!studentRecord?.lastName && student?.last_name) && isLastNameError(student.last_name),
    document_number: (!studentRecord?.documentNumber && student?.document_number) && isIdentificationError(student.document_number),
    telephone: (student?.telephone) && isPhoneError(student.telephone),
  };

  const studentIsComplete = [
    student,
    student?.name,
    student?.last_name,
    student?.email,
    student?.telephone,
    student?.email,
    student?.document_type,
    student?.document_number,
    student?.city
  ].every((i) => !!i);

  const canSave = () => {
    return [
      studentIsComplete,
      !errors.name,
      !errors.last_name,
      !errors.email,
      !errors.document_number,
      !errors.telephone,
      !errors.repeatEmail,
      user.valid || repeatEmail !== ''
    ].every((i) => !!i);
  };

  const onChangeRepeatEmail = (e: GenericEvent) => {
    setRepeatEmail(e.target.value as string);
  };

  useEffect(() => {
    if (student) {
      if (canSave()) {
        setCanSaveStudentData(true);
      } else {
        setCanSaveStudentData(false);
      }
    }
  }, [student, repeatEmail]);

  const checkDisabledPhone = () => {
    if (isLoadingUserData) {
      return true;
    }
    if (studentRecord?.phone) {
      const errorPhone = !!isPhoneError(studentRecord?.phone);
      return !errorPhone;
    }
  };

  return (
    <Box sx={{ padding: '0 16px' }} data-testid="StudentData-Container">
      <Box sx={{ m: '1rem 0', width: '100%', display: 'flex', flexWrap: 'wrap', flexGrow: '1', gap: '24px' }}>
        <FormContainer>
          <Grid item lg={4} md={4} sm={4} xs={4}>
            <SelectDocumentType
              onChange={handleStudent}
              value={student?.document_type ?? DocumentTypes.dni}
              name="document_type"
              disabled={!!(studentRecord?.documentType ?? isLoadingUserData)}
              showLoading={isLoadingUserData}
            />
          </Grid>
          <Grid item lg={8} md={8} sm={8} xs={8}>
            <TextField
              tabIndex={-1}
              className="REQXXI-StudentData-Field-DocumentNumber"
              sx={{ width: '100%' }}
              id="document-number-input"
              size="small"
              error={!!errors.document_number}
              name="document_number"
              label={t('documentNumber')}
              onChange={handleStudent}
              value={student?.document_number}
              helperText={errors.document_number}
              InputProps={{
                endAdornment: checkLoadingData(),
                inputProps: { maxLength: 11, 'data-testid': 'document-number-input', tabIndex: 7 },
                type: student?.document_type === DocumentTypes.dni ? 'number' : 'text'
              }}
              
              InputLabelProps={{
                shrink: student?.document_number || errors.document_number
              }}
              disabled={!!(studentRecord?.documentNumber ?? isLoadingUserData)}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <TextField
              tabIndex={-1}
              size="small"
              name="name"
              error={!!errors.name}
              helperText={errors.name}
              className="REQXXI-StudentData-Field"
              id="name-input"
              label={t('firstName')}
              type="text"
              onChange={handleStudent}
              value={student?.name}
              InputProps={{
                endAdornment: checkLoadingData(),
                inputProps: { 'data-testid': 'name-input', tabIndex: 8 }
              }}
              InputLabelProps={{
                shrink: student?.name || errors.name
              }}
              disabled={!!(studentRecord?.name ?? isLoadingUserData)}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            <TextField
              size="small"
              error={!!errors.last_name}
              helperText={errors.last_name}
              name="last_name"
              className="REQXXI-StudentData-Field"
              id="lastname-input"
              label={t('lastName')}
              type="text"
              onChange={handleStudent}
              value={student?.last_name}
              InputProps={{
                endAdornment: checkLoadingData(),
                inputProps: { 'data-testid': 'lastname-input', tabIndex: 9 }
              }}
              InputLabelProps={{
                shrink: student?.last_name || errors.last_name
              }}
              disabled={!!(studentRecord?.lastName ?? isLoadingUserData)}
            />
          </Grid>
        </FormContainer>
        <Grid item xs={12}>
          <Localities
            locality={student?.city}
            onChange={(city) => {
              handleStudent({
                target: {
                  name: 'city',
                  value: city
                }
              } as React.ChangeEvent<HTMLInputElement>);
            }}
            disabled={!!(!!studentRecord?.address || isLoadingUserData)}
            checkLoadingData={checkLoadingData}
          />
        </Grid>
        <FormContainer>
          <Grid item md={12} sm={12} xs={12}>
            <Phonefield
              city={student?.city}
              telephone={student?.telephone}
              disabled={checkDisabledPhone()}
              isLoadingUserData={isLoadingUserData}
              onChange={(telephone) => {
                handleStudent({ target: { name: 'telephone', value: telephone } } as React.ChangeEvent<HTMLInputElement>);
              }}
              checkLoadingData={checkLoadingData}
            />
          </Grid>
          <Grid item lg={(!studentRecord && !isLoadingUserData) ?? 6} md={(!studentRecord && !isLoadingUserData) ? 6 : 12} sm={12} xs={12}>
            <TextField
              size="small"
              name="email"
              error={!!errors.email}
              helperText={errors.email}
              autoComplete='off'
              className={'REQXXI-StudentData-Field'}
              id="email-input"
              type="email"
              label={t('email')}
              onChange={handleStudent}
              value={student?.email}
              onPaste={event => {
                event.preventDefault();
              }}
              InputProps={{
                endAdornment: checkLoadingData && checkLoadingData()
              }}
              InputLabelProps={{
                shrink: student?.email || errors.email
              }}
              inputProps={{ 'data-testid': 'email-input', tabIndex: 15 }}
              disabled={!!(studentRecord?.email ?? isLoadingUserData)}
            />
          </Grid>
          <Grid item lg={6} md={6} sm={12} xs={12}>
            {(!studentRecord && !isLoadingUserData) &&
              <TextField
                size="small"
                name="email"
                error={!!errors.repeatEmail}
                helperText={errors.repeatEmail}
                autoComplete='off'
                className="REQXXI-StudentData-Field"
                id="repeat-email-input"
                type="email"
                label={t('repeatEmail')}
                onChange={onChangeRepeatEmail}
                value={repeatEmail}
                onPaste={event => {
                  event.preventDefault();
                }}
                inputProps={{ 'data-testid': 'repeat-email-input', tabIndex: 16 }}
                InputProps={{
                  endAdornment: checkLoadingData && checkLoadingData()
                }}
                disabled={!!studentRecord}
              />
            }
          </Grid>
        </FormContainer>
      </Box>
    </Box>
  );
};

export default StudentData;
